import { ChatMessages } from "@storytell/cp-client";
import type { StreamingChatId } from "@storytell/streaming";
import { Show, createEffect, createMemo, createSignal } from "solid-js";
import { useWire } from "~/wire";

const HEARTBEAT_ACTIVITIES = ["structured_search_v1", "user_kb_search_v1"];

export const HeartbeatIndicator = (props: { chatId: StreamingChatId }) => {
  const wire = useWire();
  const stream = wire.stream;

  const [shouldRender, setShouldRender] = createSignal(false);
  const [isAnimatingOut, setIsAnimatingOut] = createSignal(false);
  const [displayedMessage, setDisplayedMessage] = createSignal("Processing...");
  const [isTextFading, setIsTextFading] = createSignal(false);

  // Check if the heartbeat operation is active
  const hasActiveHeartbeat = createMemo(() => {
    if (!props.chatId) {
      return false;
    }

    const chat = stream.chat.get(props.chatId);
    if (!chat || !chat.messages) {
      return false;
    }

    // Look for an active heartbeat operation
    for (const messageId of chat.messages) {
      const message = stream.message.get(messageId);

      if (!message || !message.operations || !Array.isArray(message.operations)) {
        continue;
      }

      for (const operationId of message.operations) {
        const operation = stream.operation.get(operationId);

        if (!operation) {
          continue;
        }

        // Check if this is an ACTIVE heartbeat operation
        if (!operation.isComplete && operation.activity === "system_heartbeat") {
          return true;
        }
      }
    }

    return false;
  });

  // Check if there are any OTHER operations (non-heartbeat) actively streaming
  const hasOtherStreamingOperations = createMemo(() => {
    if (!props.chatId) {
      return false;
    }

    const chat = stream.chat.get(props.chatId);
    if (!chat || !chat.messages) {
      return false;
    }

    // Check all messages for streaming operations
    for (const messageId of chat.messages) {
      const message = stream.message.get(messageId);
      if (!message || !message.operations || !Array.isArray(message.operations)) {
        continue;
      }

      // Check each operation in the message
      for (const operationId of message.operations) {
        const operation = stream.operation.get(operationId);
        if (!operation) {
          continue;
        }

        // Skip the heartbeat operation itself and operations that should show heartbeat
        if (operation.activity === "system_heartbeat" || HEARTBEAT_ACTIVITIES.includes(operation.activity)) {
          continue;
        }

        // Check if this operation is actively running/streaming
        // An operation is "streaming" if it's not complete OR has status RUNNING
        if (!operation.isComplete || operation.status === ChatMessages.OperationStatus.RUNNING) {
          return true;
        }
      }
    }

    return false;
  });

  // Get the heartbeat message from the backend operation
  const getHeartbeatMessage = createMemo(() => {
    if (!props.chatId) {
      return "Processing...";
    }

    const chat = stream.chat.get(props.chatId);
    if (!chat || !chat.messages) {
      return "Processing...";
    }

    // Find the heartbeat operation in the current messages
    for (const messageId of chat.messages) {
      const message = stream.message.get(messageId);
      if (!message || !message.operations || !Array.isArray(message.operations)) {
        continue;
      }

      for (const operationId of message.operations) {
        const operation = stream.operation.get(operationId);
        if (!operation) {
          continue;
        }

        // Check if this is an active heartbeat operation
        if (!operation.isComplete && operation.activity === "system_heartbeat") {
          return operation.progressMessage || operation.name || "Processing...";
        }
      }
    }

    return "Processing...";
  });

  // Effect to handle smooth phrase rotation animation
  createEffect(() => {
    const newMessage = getHeartbeatMessage();

    if (newMessage !== displayedMessage()) {
      // Fade out current text
      setIsTextFading(true);

      // Wait for fade-out (150ms), then update text and fade in
      setTimeout(() => {
        setDisplayedMessage(newMessage);
        setIsTextFading(false);
      }, 150);
    }
  });

  // Effect to track heartbeat status with exit animation delay
  createEffect(() => {
    const isActive = hasActiveHeartbeat();
    const hasOtherStreaming = hasOtherStreamingOperations();
    const isChatStreaming = wire.chat.isChatStreaming();

    // Only show heartbeat if it's active AND no other operations are streaming AND chat is streaming
    if (isActive && !hasOtherStreaming && isChatStreaming) {
      setShouldRender(true);
      setIsAnimatingOut(false);
    } else if (shouldRender()) {
      setIsAnimatingOut(true);
      setTimeout(() => {
        setShouldRender(false);
        setIsAnimatingOut(false);
      }, 300);
    }
  });

  // Bouncing dots indicator with enhanced animation
  const renderIndicator = () => {
    const bounceKeyframes = `
      @keyframes bounceDot {
        0%, 100% {
          transform: translateY(0);
          animation-timing-function: cubic-bezier(0.8, 0, 1, 1);
        }
        50% {
          transform: translateY(-4px);
          animation-timing-function: cubic-bezier(0, 0, 0.2, 1);
        }
      }
    `;

    return (
      <>
        <style>{bounceKeyframes}</style>
        <div class="flex items-center gap-1">
          <div
            class="w-1 h-1 rounded-full bg-secondary-text"
            style={{
              animation: "bounceDot 0.6s infinite",
              "animation-delay": "0s",
            }}
          />
          <div
            class="w-1 h-1 rounded-full bg-secondary-text"
            style={{
              animation: "bounceDot 0.6s infinite",
              "animation-delay": "0.1s",
            }}
          />
          <div
            class="w-1 h-1 rounded-full bg-secondary-text"
            style={{
              animation: "bounceDot 0.6s infinite",
              "animation-delay": "0.2s",
            }}
          />
        </div>
      </>
    );
  };

  return (
    <Show when={shouldRender()}>
      <div
        role="status"
        aria-live="polite"
        aria-label="Processing indicator"
        class="mt-4"
        classList={{
          "animate-in fade-in duration-500": !isAnimatingOut(),
          "animate-out fade-out duration-300": isAnimatingOut(),
        }}
      >
        <div class="inline-flex items-center gap-3 px-4 py-2 bg-canvas-secondary/30 backdrop-blur-sm rounded-lg">
          {renderIndicator()}
          <span
            class="text-sm text-secondary-text transition-opacity duration-300"
            classList={{
              "opacity-0": isTextFading(),
              "opacity-100": !isTextFading(),
            }}
          >
            {displayedMessage()}
          </span>
        </div>
      </div>
    </Show>
  );
};
